import React, {useRef, useEffect, useState} from "react";
import mapboxgl from "mapbox-gl";
import MapboxGeocoding from '@mapbox/mapbox-sdk/services/geocoding';
import 'mapbox-gl/dist/mapbox-gl.css';
import {Box, Input, List, ListItem} from "@chakra-ui/react";

const geocodingClient = MapboxGeocoding({accessToken: process.env.REACT_APP_MAPBOX_TOKEN});

export default function Mapbox({coordinates = {lng: 13.404954, lat: 52.520008}, onAddressChange}) {

    const mapContainerRef = useRef(null);
    const [mapInstance, setMapInstance] = useState(null); // Track map instance
    const [marker, setMarker] = useState(null); // Track marker

    const [query, setQuery] = useState('');
    const [address, setAddress] = useState(null);
    const [suggestions, setSuggestions] = useState([]);


    useEffect(() => {
        // Set your mapbox access token here
        mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

        // Initialize the map
        const map = new mapboxgl.Map({
            container: mapContainerRef.current, // reference to the HTML container
            style: 'mapbox://styles/mapbox/streets-v11', // Map style
            center: coordinates, // Initial map center [lng, lat]
            zoom: 9, // Initial zoom level
        });

        // Add navigation controls to the map
        map.addControl(new mapboxgl.NavigationControl(), 'top-right');

        // Set the map instance for later use
        setMapInstance(map);

        // Cleanup on unmount
        return () => map.remove();
    }, []);

    const handleAddressChange = (address) => {
        if (marker) {
            marker.remove();
        }

        // Center the map on the new coordinates
        if (mapInstance) {
            mapInstance.jumpTo({center: address.coordinates, zoom: 14});

            // Add a new marker at the new coordinates and store it in state
            const newMarker = new mapboxgl.Marker().setLngLat(address.coordinates).addTo(mapInstance);
            setMarker(newMarker);
        }

        // Pass the coordinates back to the parent component
        if (onAddressChange) {
            onAddressChange(address);
        }
    };

    useEffect(() => {
        if (address) {
            handleAddressChange(address);
        }
    }, [address]);

    // Handle input change and fetch geocoding suggestions
    const handleInputChange = async (e) => {
        const userInput = e.target.value;
        setQuery(userInput);

        if (userInput.length > 2) {
            const response = await geocodingClient
                .forwardGeocode({
                    query: userInput,
                    autocomplete: true,
                    limit: 5,
                })
                .send();

            const results = response.body.features.map((feature) => ({
                placeName: feature.place_name,
                coordinates: feature.geometry.coordinates,
                context: feature.context,
                street: feature.text,
                street_nr: feature.address,
            }));

            setSuggestions(results);
        } else {
            setSuggestions([]);
        }
    };

    // Extract street, ZIP code, place, and country
    const extractAddressDetails = (context) => {
        let postcode = '';
        let place = '';
        let country = '';

        context.forEach((item) => {
            if (item.id.startsWith('postcode')) {
                postcode = item.text;
            }
            if (item.id.startsWith('place')) {
                place = item.text;
            }
            if (item.id.startsWith('country')) {
                country = item.text;
            }
        });

        return {postcode, place, country};
    };

    const handleSuggestionClick = (suggestion) => {
        const {postcode, place, country} = extractAddressDetails(suggestion.context);
        const selectedAddress = {
            placeName: suggestion.placeName,
            street: suggestion.street + (suggestion.street_nr ? ' ' + suggestion.street_nr : ''),
            postcode,
            place,
            country,
            coordinates: suggestion.coordinates,
        };

        setQuery(suggestion.placeName);
        setAddress(selectedAddress);
        setSuggestions([]);
    };

    return (
        <div>
            {address === null ?
                <Input
                    placeholder="Gib deine Adresse ein"
                    value={query}
                    onChange={handleInputChange}
                /> : ''
            }
            {suggestions.length > 0 && (
                <List
                    position="absolute"
                    width="100%"
                    bg="white"
                    boxShadow="md"
                    zIndex={10}
                    maxHeight="200px"
                    overflowY="auto"
                >
                    {suggestions.map((suggestion, index) => (
                        <ListItem
                            key={index}
                            padding="8px"
                            cursor="pointer"
                            _hover={{bg: 'gray.100'}}
                            onClick={() => handleSuggestionClick(suggestion)}
                        >
                            {suggestion.placeName}
                        </ListItem>
                    ))}
                </List>
            )}
            <Box mt={5}>
                <div ref={mapContainerRef} style={{width: '100%', height: '500px'}}/>
            </Box>
        </div>
    )
        ;
}
